import React, { useState, useMemo } from 'react'
import { ChevronDown, ChevronUp, ChevronsUpDown, ArrowUpDown } from 'lucide-react'

export interface ColumnDefinition<T = any> {
  key: string
  label: string
  render?: (value: any, row: T) => React.ReactNode
  sortable?: boolean
  align?: 'left' | 'right' | 'center'
  width?: string
  className?: string
}

export interface AnalyticsDetailTableProps<T = any> {
  columns: ColumnDefinition<T>[]
  data: T[]
  keyExtractor: (row: T, index: number) => string | number

  // Pagination
  rowsPerPage?: number
  showPagination?: boolean

  // Sorting
  defaultSortBy?: string
  defaultSortOrder?: 'asc' | 'desc'

  // Styling
  className?: string
  emptyMessage?: string

  // Row interaction
  onRowClick?: (row: T) => void
  highlightOnHover?: boolean
}

export function AnalyticsDetailTable<T = any>({
  columns,
  data,
  keyExtractor,
  rowsPerPage = 10,
  showPagination = true,
  defaultSortBy,
  defaultSortOrder = 'desc',
  className = '',
  emptyMessage = 'Nenhum dado disponível',
  onRowClick,
  highlightOnHover = true
}: AnalyticsDetailTableProps<T>) {
  // Sorting state
  const [sortBy, setSortBy] = useState<string | null>(defaultSortBy || null)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(defaultSortOrder)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)

  // Handle column sort
  const handleSort = (columnKey: string) => {
    const column = columns.find(c => c.key === columnKey)
    if (!column?.sortable) return

    if (sortBy === columnKey) {
      // Toggle order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // New column
      setSortBy(columnKey)
      setSortOrder('desc')
    }

    // Reset to first page when sorting changes
    setCurrentPage(1)
  }

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortBy) return data

    return [...data].sort((a, b) => {
      const aValue = a[sortBy]
      const bValue = b[sortBy]

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0
      if (aValue == null) return 1
      if (bValue == null) return -1

      // Numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue
      }

      // String comparison
      const aStr = String(aValue).toLowerCase()
      const bStr = String(bValue).toLowerCase()
      const comparison = aStr.localeCompare(bStr)

      return sortOrder === 'asc' ? comparison : -comparison
    })
  }, [data, sortBy, sortOrder])

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!showPagination) return sortedData

    const startIndex = (currentPage - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return sortedData.slice(startIndex, endIndex)
  }, [sortedData, currentPage, rowsPerPage, showPagination])

  const totalPages = Math.ceil(sortedData.length / rowsPerPage)

  // Render sort icon
  const renderSortIcon = (columnKey: string) => {
    if (sortBy !== columnKey) {
      return <ChevronsUpDown size={14} className="text-brand-gray/50" />
    }
    return sortOrder === 'asc'
      ? <ChevronUp size={14} className="text-brand-primary" />
      : <ChevronDown size={14} className="text-brand-primary" />
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className="bg-brand-dark-light rounded-monumenta border border-brand-gray/10 p-12">
        <div className="text-center text-brand-gray">
          {emptyMessage}
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Table */}
      <div className="bg-brand-dark-light rounded-monumenta border border-brand-gray/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-brand-dark border-b border-brand-gray/10">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`
                      px-6 py-3 text-xs font-medium text-brand-gray uppercase tracking-wider
                      ${column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'}
                      ${column.sortable ? 'cursor-pointer select-none hover:text-white transition-colors' : ''}
                      ${column.width ? column.width : ''}
                    `}
                    style={{ width: column.width }}
                    onClick={() => column.sortable && handleSort(column.key)}
                  >
                    <div className={`flex items-center gap-2 ${
                      column.align === 'right' ? 'justify-end' :
                      column.align === 'center' ? 'justify-center' :
                      'justify-start'
                    }`}>
                      <span>{column.label}</span>
                      {column.sortable && renderSortIcon(column.key)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-gray/10">
              {paginatedData.map((row, index) => {
                const rowKey = keyExtractor(row, index)
                return (
                  <tr
                    key={rowKey}
                    onClick={() => onRowClick?.(row)}
                    className={`
                      ${highlightOnHover ? 'hover:bg-brand-dark/50 transition-colors' : ''}
                      ${onRowClick ? 'cursor-pointer' : ''}
                    `}
                  >
                    {columns.map((column) => {
                      const value = row[column.key]
                      const displayValue = column.render
                        ? column.render(value, row)
                        : value

                      return (
                        <td
                          key={column.key}
                          className={`
                            px-6 py-4 text-sm
                            ${column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'}
                            ${column.className || 'text-white'}
                          `}
                        >
                          {displayValue}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <div className="flex items-center justify-between px-2">
          {/* Info */}
          <div className="text-sm text-brand-gray">
            Mostrando{' '}
            <span className="text-white font-medium">
              {(currentPage - 1) * rowsPerPage + 1}
            </span>
            {' '}a{' '}
            <span className="text-white font-medium">
              {Math.min(currentPage * rowsPerPage, sortedData.length)}
            </span>
            {' '}de{' '}
            <span className="text-white font-medium">
              {sortedData.length}
            </span>
            {' '}resultados
          </div>

          {/* Page controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded-monumenta text-sm bg-brand-dark-light text-white
                       disabled:opacity-30 disabled:cursor-not-allowed
                       hover:bg-brand-dark transition-colors"
            >
              Primeira
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded-monumenta text-sm bg-brand-dark-light text-white
                       disabled:opacity-30 disabled:cursor-not-allowed
                       hover:bg-brand-dark transition-colors"
            >
              Anterior
            </button>

            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number

                // Calculate which pages to show (show current page in middle when possible)
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`
                      w-8 h-8 rounded-monumenta text-sm font-medium transition-colors
                      ${currentPage === pageNum
                        ? 'bg-brand-primary text-brand-dark'
                        : 'bg-brand-dark-light text-white hover:bg-brand-dark'
                      }
                    `}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded-monumenta text-sm bg-brand-dark-light text-white
                       disabled:opacity-30 disabled:cursor-not-allowed
                       hover:bg-brand-dark transition-colors"
            >
              Próxima
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded-monumenta text-sm bg-brand-dark-light text-white
                       disabled:opacity-30 disabled:cursor-not-allowed
                       hover:bg-brand-dark transition-colors"
            >
              Última
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
